import type { TowerDesign } from "@/game/towerSpec";
import type { Section } from "@/physics/model";
import type { MountedLoad } from "@/game/appurtenances";
import { CATALOG } from "@/game/appurtenances";

/**
 * SUTRO TOWER — San Francisco's 977 ft (297.8 m) self-supporting steel broadcast
 * landmark on Mount Sutro (completed 1973); the huge orange-and-white tower that
 * looms over the city in Watch Dogs 2. No stamped drawing exists in
 * reference_towers/, so this is approximated from public facts — but now built to
 * actually LOOK like Sutro using the crossarm/profile features:
 *   - THREE-legged tripod (faces: 3), wide splayed base converging to a slim
 *     central mast (the `radii` profile), then a thin spire — Sutro's real form.
 *   - Its signature TWIN ANTENNA "CANDELABRA": two big horizontal platforms in
 *     the upper third (the `crossarms`), carrying the VHF/UHF TV + FM antennas.
 *   - ~3.7 million lb (~1,700 t) of steel; founded on Franciscan bedrock.
 *
 * Accuracy to the real silhouette is the goal here — it is a 298 m self-supporting
 * pin-lattice, so it is very flexible and may sway/topple in the sim; that's fine
 * (physics is being tuned separately). Grade A572 (1970s high-strength steel).
 */

// A 298 m tower's built-up legs dwarf the pipe catalog — model a ~1 m thin-wall
// box column (low A to limit the gravity transient, high I for stiffness).
const SUTRO_LEG: Section = { name: "Sutro built-up leg (~1 m box)", A: 0.032, I: 3.0e-3, dragWidth: 0.3 };
// A "brace" here spans a whole ~46 m face of the wide 3-legged base, which in
// reality is a SUB-BRACED lattice panel (not one strut). Modelled with built-up
// (high-I) capacity so those long members don't buckle at ~7 kN.
const SUTRO_BRACE: Section = { name: "Sutro built-up brace panel", A: 0.008, I: 0.03, dragWidth: 0.12 };
// The candelabra decks read as chunky horizontal trusses.
const CANDELABRA: Section = { name: "Sutro candelabra truss", A: 0.014, I: 0.02, dragWidth: 0.28 };
// The three tall antenna prongs of the top "trident".
const ANTENNA: Section = { name: "Sutro antenna prong", A: 0.004, I: 5e-5, dragWidth: 0.15 };

const HEIGHT = 297.8; // 977 ft
const PANELS = 40; // ~7.4 m panels

// The REAL Sutro profile — an hourglass, from the Sutro Tower DEIR (p.20) + the
// ukd1/openscad-sutro-tower model. Leg-to-leg spacing at each level → triangular
// circumradius R = spacing / √3:
//   base (0 m):    legs 45.7 m apart → R 26.4 m   (wide splayed base)
//   waist (165 m): legs 18.3 m apart → R 10.6 m   (legs converge — level 4)
//   flare (228 m): legs 30.5 m apart → R 17.6 m   (legs splay back OUT — level 6)
//   top (297.8 m): tapers to the antenna spire     (~2.5 m)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
function profileRadius(y: number): number {
  if (y <= 165.2) return lerp(26.4, 10.57, y / 165.2); // base → waist
  if (y <= 227.7) return lerp(10.57, 17.6, (y - 165.2) / (227.7 - 165.2)); // waist → candelabra flare
  return lerp(17.6, 1.0, (y - 227.7) / (HEIGHT - 227.7)); // flare → thin needle (the trident takes over)
}
const radii = Array.from({ length: PANELS + 1 }, (_, i) => profileRadius((i / PANELS) * HEIGHT));

const AZ = [0, (2 * Math.PI) / 3, (4 * Math.PI) / 3]; // 3 faces

// Broadcast antennas hung on the two candelabra levels (~208 m and ~238 m).
const antennas: MountedLoad[] = [
  ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: 208, azimuth })),
  ...AZ.map((azimuth) => ({ appurtenance: CATALOG.panelSector, height: 238, azimuth })),
  { appurtenance: CATALOG.coaxBundle, height: 238, runLength: 238 },
];

export const design: TowerDesign = {
  id: "sutro-tower",
  name: "Sutro Tower (San Francisco)",
  description: "977 ft self-supporting SF broadcast landmark (the Watch Dogs 2 tower). Publicly approximated.",
  author: "iconic tower — approximated from public data (no stamped drawing)",
  spec: {
    type: "lattice",
    height: 297.8, // 977 ft
    stack: {
      faces: 3, // three-legged tripod
      panels: PANELS,
      baseRadius: 26.4, // real base (legs 45.7 m apart) — overridden by radii, kept for validation
      topRadius: 2.5,
      radii, // hourglass: wide base → waist → candelabra flare → spire
      legSection: SUTRO_LEG,
      braceSection: SUTRO_BRACE,
      legMaterial: "a572", // 1970s high-strength structural steel
      braceMaterial: "a572",
      bracing: "X",
    },
    // The real DEIR platform schedule (levels 2–6, p.20), as horizontal decks —
    // the two prominent candelabra are the level-4 (waist) and level-6 platforms.
    crossarms: [
      { atHeightFrac: 57.0 / HEIGHT, radius: 22, section: CANDELABRA, material: "a572" }, // level 2
      { atHeightFrac: 116.4 / HEIGHT, radius: 17, section: CANDELABRA, material: "a572" }, // level 3
      { atHeightFrac: 167.0 / HEIGHT, radius: 12.5, section: CANDELABRA, material: "a572" }, // level 4 (waist)
      { atHeightFrac: 200.2 / HEIGHT, radius: 16, section: CANDELABRA, material: "a572" }, // level 5
      { atHeightFrac: 230.0 / HEIGHT, radius: 19, section: CANDELABRA, material: "a572" }, // level 6
    ],
    // the signature TRIDENT — three tall antenna prongs crowning the tower from
    // level 6 (232 m / 762 ft) up to the 297.8 m tip
    masts: [
      {
        atHeightFrac: 232 / HEIGHT,
        count: 3,
        spread: 3.5,
        topSpread: 2.2,
        rise: 65.8,
        panels: 8,
        section: ANTENNA,
        material: "a572",
      },
    ],
    foundation: {
      soil: "rock", // Franciscan bedrock, Mount Sutro
      base: { type: "pad", width: 6.0, diameter: 1.0, depth: 3.5, concrete: "c40" },
      connection: {
        kind: "anchorBolts",
        nBolts: 24,
        bolt: "b2_25",
        grade: "a615_75",
        boltCircleDia: 2.0,
        embedment: 0.9,
        edgeDistance: 0.6,
        platePlanArea: 4.0,
        plateThickness: 0.09,
        plateGrade: "a36rod",
        fc: 40e6,
      },
    },
    loads: antennas,
  },
};
